/**
 * PF SmartCart bridge
 * - Keeps existing SmartCart "peek" logic (if injected by Rebuy) intact
 * - Ensures a manual cart icon click always opens SmartCart (when available)
 * - Avoids the theme cart drawer / /cart navigation when SmartCart is present
 */
(() => {
  // Prevent double-binding if this script is injected more than once.
  if (window.__PF_SMARTCART_BRIDGE_LOADED__) return;
  window.__PF_SMARTCART_BRIDGE_LOADED__ = true;

  /**
   * ================================
   * Common Lands SmartCart Bridge Config
   * ================================
   * Update these values to change behavior without digging through the file.
   */
  const PF_SC_CONFIG = {
    /**
     * Add-to-cart gating schedule.
     * - **exact**: show on these add counts (e.g. [1, 3, 5, 19])
     * - **atOrAfter**: show on this count and every add after it (e.g. 20 => 20, 21, 22, ...)
     */
    showOnAddCounts: {
      exact: [1, 5, 10, 20],
      atOrAfter: 50,
    },

    /** Mobile peek behavior (when an add is allowed). */
    mobilePeek: {
      peekMs: 2200,
      slideMs: 300, // keep in sync with CSS transition duration
    },

    /** Tap-to-edit quantity label behavior. */
    qtyEdit: {
      enabled: true,
      min: 1,
      maxFallback: 999,
      networkDebounceMs: 250,
      commitOnBlur: true,
      commitOnEnter: true,
      cancelOnEscape: true,
      // Ignore next N cart change events after a direct quantity commit
      // so nth-add gating does not interpret this as a product "add".
      ignoreCartChangeEventsAfterCommit: 2,
    },

    /**
     * VIP progress messaging.
     * - Edit `tiers` to add/rename tiers + CSS classes.
     * - Edit `alwaysFreeTiers` to mark tiers that always get free shipping messaging.
     */
    vip: {
      alwaysFreeTiers: ['3'],
      tiers: {
        '0': { name: 'Villager', className: 'pf-vip-title--villager' },
        '1': { name: 'Adventurer', className: 'pf-vip-title--adventurer' },
        '2': { name: 'Guardian', className: 'pf-vip-title--guardian' },
        '3': { name: 'Champion', className: 'pf-vip-title--champion' },
      },
      templates: {
        freeShippingHtml: '<span class="pf-free-shipping">FREE SHIPPING</span>',
        alwaysFree:
          'Welcome back <span class="pf-vip-title {tierClass}">{tierName}</span>, you get {freeShipping}!',
        reached:
          'Congrats <span class="pf-vip-title {tierClass}">{tierName}</span>, you get {freeShipping}!',
        away:
          'Hey <span class="pf-vip-title {tierClass}">{tierName}</span>, you&apos;re <span class="pf-threshold-price">${awayDollars}</span> away from {freeShipping}!',
      },
      // Used only if the cart attribute is missing/invalid.
      defaultThresholdCents: 30000,
    },
  };

  const HTML = document.documentElement;
  const OPEN_CLASS = 'pf-sc-open';
  const MANUAL_CLASS = 'pf-sc-manual-open';
  const OVERLAY_CLASS = 'pf-sc-overlay-on';
  const PEEK_IN_CLASS = 'pf-sc-peek-in';
  const PEEK_OUT_CLASS = 'pf-sc-peek-out';
  const PEEK_EXPANDED_CLASS = 'pf-sc-peek-expanded';
  const PF_SC_LAST_QTY_KEY = 'pf_sc_last_qty';
  const PF_SC_ADD_COUNT_KEY = 'pf_sc_add_count';
  const SC_GATE_DELAY_MS = 300; // allow SmartCart.items() to reflect the updated cart on events
  // Delay marking "open" for non-manual opens so we don't fight SmartCart "peek" animations,
  // which often set peek classes ~300ms after a cart change.
  const OPEN_MARK_DELAY_MS = 360;
  const OVERLAY_MARK_DELAY_MS = 140; // prevents scrim flash on suppressed auto-opens
  let openMarkT = null;
  let overlayMarkT = null;
  let desktopAddAllowed = true;
  let gateT = null;
  let lastGateAction = null;
  let activeQtyEditor = null;
  let qtyEditIgnoreCartChanges = 0;
  const qtyMutationStateByKey = new Map();
  const QTY_MUTATION_SETTLE_FALLBACK_MS = 1200;

  function hasSmartCart() {
    return (
      typeof window.Rebuy !== 'undefined' &&
      window.Rebuy &&
      window.Rebuy.SmartCart &&
      typeof window.Rebuy.SmartCart.show === 'function'
    );
  }

  function clearPeekState() {
    HTML.classList.remove(PEEK_IN_CLASS, PEEK_OUT_CLASS);
    try {
      clearTimeout(window.__PF_SC_PEEK_T);
      clearTimeout(window.__PF_SC_PEEK_HIDE_T);
    } catch (_) {}
  }

  function isMobile() {
    return window.matchMedia && window.matchMedia('(max-width: 768px)').matches;
  }

  function isPeekingNow() {
    return HTML.classList.contains(PEEK_IN_CLASS) || HTML.classList.contains(PEEK_OUT_CLASS);
  }

  function scTotalQty() {
    try {
      const items = window.Rebuy?.SmartCart?.items?.() || [];
      return items.reduce((sum, item) => sum + (item?.quantity || 0), 0);
    } catch (_) {
      return 0;
    }
  }

  function initQtyBaseline() {
    try {
      const qty = scTotalQty();
      sessionStorage.setItem(PF_SC_LAST_QTY_KEY, String(qty));
    } catch (_) {}
  }

  function shouldShowOnNthAdd(n) {
    const exact = PF_SC_CONFIG?.showOnAddCounts?.exact || [];
    const atOrAfter = PF_SC_CONFIG?.showOnAddCounts?.atOrAfter;
    if (Array.isArray(exact) && exact.includes(n)) return true;
    if (Number.isFinite(atOrAfter) && typeof atOrAfter === 'number' && n >= atOrAfter) return true;
    return false;
  }

  function normalizeQty(value) {
    const n = parseInt(String(value), 10);
    return Number.isFinite(n) ? n : null;
  }

  /* ================================
     1) Quantity limits via product tags
     Tags supported: limit-1, limit-2, limit-3, limit-4, limit-5, limit-10, ...
     ================================ */
  const LIMIT_TAG_RE = /^limit-(\d+)$/;
  let qtyLimitT = null;
  let qtyLimitRunning = false;

  function limitFromTags(tags) {
    if (typeof tags === 'string') tags = tags.split(',').map((t) => t.trim());
    if (!Array.isArray(tags)) return null;
    let limit = null;
    for (const tag of tags) {
      const m = LIMIT_TAG_RE.exec(String(tag));
      if (!m) continue;
      const n = parseInt(m[1], 10);
      if (!Number.isFinite(n) || n <= 0) continue;
      limit = limit == null ? n : Math.min(limit, n);
    }
    return limit;
  }

  function enforceItemQuantityLimits() {
    if (qtyLimitRunning) return;
    qtyLimitRunning = true;
    try {
      if (!hasSmartCart()) {
        console.log('[PF Limits] SmartCart not available');
        return;
      }
      const cartItems = window.Rebuy.SmartCart.items?.() || [];
      console.log('[PF Limits] Cart items:', cartItems.length);
      for (const item of cartItems) {
        const tags = item?.product?.tags;
        const limit = limitFromTags(tags);
        console.log('[PF Limits] Product:', item?.product?.title, '| Tags:', tags, '| Limit:', limit);
        if (limit == null) continue;
        const mutationKey = smartCartItemPrimaryKey(item);
        const mutationState = mutationKey ? qtyMutationStateByKey.get(mutationKey) : null;
        if (mutationState?.inFlight || mutationState?.debounceT) continue;
        const qty = Number(item?.quantity || 0);
        if (qty > limit) {
          console.log('[PF Limits] Enforcing limit:', qty, '->', limit);
          if (mutationState && Number.isFinite(mutationState.desiredQty)) {
            mutationState.desiredQty = Math.min(mutationState.desiredQty, limit);
          }
          try {
            window.Rebuy?.Cart?.setItemQuantity?.(item, limit);
          } catch (e) {
            console.error('[PF Limits] setItemQuantity failed:', e);
          }
        }
      }
    } finally {
      qtyLimitRunning = false;
    }
  }

  function scheduleItemQuantityLimits() {
    if (qtyLimitT) clearTimeout(qtyLimitT);
    // Delay so Rebuy.SmartCart.items() reflects the updated cart
    qtyLimitT = setTimeout(enforceItemQuantityLimits, 320);
  }

  /* ================================
     1b) Inline quantity tap-to-edit
     ================================ */
  function qtyLabelSelector() {
    return '.rebuy-cart__flyout-item-quantity-widget-label';
  }

  function qtyWidgetSelector() {
    return '.rebuy-cart__flyout-item-quantity-widget';
  }

  function getCartItemsRoot() {
    return document.querySelector('.rebuy-cart__flyout [data-rebuy-component="cart-items"]');
  }

  function isInsideSmartCartFlyout(el) {
    return !!(el && el.closest && el.closest('.rebuy-cart__flyout'));
  }

  function cartItemRows() {
    const root = getCartItemsRoot();
    if (!root) return [];
    const rows = Array.from(root.querySelectorAll('li'));
    return rows.filter((row) => row.querySelector(qtyLabelSelector()));
  }

  function cartItemIdentityCandidates(el) {
    const vals = new Set();
    const tryAttrs = [
      'data-line-item-id',
      'data-rebuy-line-item-id',
      'data-key',
      'data-cart-item-key',
      'data-item-key',
      'data-variant-id',
    ];
    const nodes = [el, ...(el ? Array.from(el.querySelectorAll('*')) : [])];
    for (const node of nodes) {
      if (!node || !node.getAttribute) continue;
      for (const attr of tryAttrs) {
        const val = node.getAttribute(attr);
        if (val) vals.add(String(val));
      }
    }
    return Array.from(vals);
  }

  function resolveSmartCartItemFromLabel(labelEl) {
    const items = window.Rebuy?.SmartCart?.items?.() || [];
    if (!items.length || !labelEl) return null;

    const row = labelEl.closest('li');
    if (!row) return null;
    return resolveSmartCartItemFromRow(row, items);
  }

  function resolveSmartCartItemFromRow(row, itemsOverride) {
    const items = itemsOverride || window.Rebuy?.SmartCart?.items?.() || [];
    if (!items.length || !row) return null;

    const candidates = cartItemIdentityCandidates(row);
    if (candidates.length) {
      const direct = items.find((item) => {
        const keys = [
          item?.id,
          item?.line_item_id,
          item?.variant_id,
          item?.variant?.id,
          item?.key,
          item?.item_key,
        ]
          .filter((v) => v != null)
          .map((v) => String(v));
        return candidates.some((c) => keys.includes(c));
      });
      if (direct) return direct;
    }

    const rows = cartItemRows();
    const rowIndex = rows.indexOf(row);
    if (rowIndex >= 0 && rowIndex < items.length) return items[rowIndex];
    return null;
  }

  function resolveSmartCartItemFromQtyWidget(widgetEl) {
    const row = widgetEl?.closest?.('li');
    if (!row) return null;
    return resolveSmartCartItemFromRow(row);
  }

  function smartCartItemIdentityValues(item) {
    if (!item) return [];
    return Array.from(
      new Set(
        [
          item?.key,
          item?.item_key,
          item?.line_item_id,
          item?.id,
          item?.line,
          item?.variant_id,
          item?.variant?.id,
        ]
          .filter((v) => v != null)
          .map((v) => String(v))
      )
    );
  }

  function smartCartItemPrimaryKey(item) {
    const keys = smartCartItemIdentityValues(item);
    return keys.length ? keys[0] : null;
  }

  function resolveSmartCartItemByKey(key) {
    if (!key) return null;
    const want = String(key);
    const items = window.Rebuy?.SmartCart?.items?.() || [];
    return items.find((item) => smartCartItemIdentityValues(item).includes(want)) || null;
  }

  function findQtyWidgetByItemKey(key) {
    if (!key) return null;
    const root = getCartItemsRoot();
    if (!root) return null;
    const widgets = Array.from(root.querySelectorAll(qtyWidgetSelector()));
    for (const widget of widgets) {
      const widgetItem = resolveSmartCartItemFromQtyWidget(widget);
      if (!widgetItem) continue;
      if (smartCartItemIdentityValues(widgetItem).includes(String(key))) return widget;
    }
    return null;
  }

  function qtyMutationDebounceMs() {
    const ms = Number(PF_SC_CONFIG?.qtyEdit?.networkDebounceMs);
    return Number.isFinite(ms) && ms >= 0 ? ms : 250;
  }

  function clearQtyMutationSettleTimer(state) {
    if (!state?.settleT) return;
    clearTimeout(state.settleT);
    state.settleT = null;
  }

  function clearQtyMutationDebounceTimer(state) {
    if (!state?.debounceT) return;
    clearTimeout(state.debounceT);
    state.debounceT = null;
  }

  function ensureQtyMutationState(item) {
    const key = smartCartItemPrimaryKey(item);
    if (!key) return null;
    let state = qtyMutationStateByKey.get(key);
    if (!state) {
      state = {
        key,
        desiredQty: null,
        inFlight: false,
        inFlightQty: null,
        inFlightVersion: 0,
        version: 0,
        debounceT: null,
        settleT: null,
        lastItem: item,
        authoritativeSyncInFlight: false,
        preferDesiredUntilServerMatch: false,
        cartItemKey: null,
        variantId: null,
        productId: null,
      };
      qtyMutationStateByKey.set(key, state);
    }
    if (item) {
      state.lastItem = item;
      state.cartItemKey = item?.key != null ? String(item.key) : item?.item_key != null ? String(item.item_key) : state.cartItemKey;
      const variantIdRaw = item?.variant_id ?? item?.variant?.id;
      if (variantIdRaw != null) state.variantId = String(variantIdRaw);
      const productIdRaw = item?.product_id ?? item?.product?.id;
      if (productIdRaw != null) state.productId = String(productIdRaw);
    }
    return state;
  }

  function desiredQtyForItem(item) {
    const key = smartCartItemPrimaryKey(item);
    if (!key) return null;
    const state = qtyMutationStateByKey.get(key);
    return Number.isFinite(state?.desiredQty) ? state.desiredQty : null;
  }

  function hasActiveQtyMutations() {
    for (const state of qtyMutationStateByKey.values()) {
      if (state?.inFlight || state?.debounceT || state?.authoritativeSyncInFlight) return true;
    }
    return false;
  }

  function renderQtyWidgetFromItem(widget, item) {
    if (!widget || !item) return;
    const serverQty = Number(item?.quantity || 0);
    const key = smartCartItemPrimaryKey(item);
    const state = key ? qtyMutationStateByKey.get(key) : null;
    const hasDesired = Number.isFinite(state?.desiredQty);
    const preferDesired =
      hasDesired &&
      (state?.inFlight ||
        state?.debounceT ||
        state?.authoritativeSyncInFlight ||
        (state?.preferDesiredUntilServerMatch && serverQty !== state?.desiredQty));
    const qty = preferDesired ? state.desiredQty : serverQty;
    const label = widget.querySelector(qtyLabelSelector());
    if (label && (!activeQtyEditor || activeQtyEditor.widget !== widget)) {
      label.textContent = String(qty);
    }
    const rawLimit = limitFromTags(item?.product?.tags);
    const limit = Number.isFinite(rawLimit) ? rawLimit : null;
    const locked = limit != null && qty >= limit;
    setPlusLockStateForWidget(widget, locked, limit);
  }

  function syncQtyWidgetFromItem(item, keyOverride = null) {
    if (!item) return;
    const key = keyOverride || smartCartItemPrimaryKey(item);
    if (!key) return;
    const widget = findQtyWidgetByItemKey(key);
    if (!widget) return;
    renderQtyWidgetFromItem(widget, item);
  }

  function syncQtyWidgetFromResolvedQty(key, qty) {
    if (!Number.isFinite(qty)) return;
    const widget = findQtyWidgetByItemKey(key);
    if (!widget) return;
    const label = widget.querySelector(qtyLabelSelector());
    if (label && (!activeQtyEditor || activeQtyEditor.widget !== widget)) {
      label.textContent = String(qty);
    }
    const item = resolveSmartCartItemFromQtyWidget(widget);
    const rawLimit = limitFromTags(item?.product?.tags);
    const limit = Number.isFinite(rawLimit) ? rawLimit : null;
    const locked = limit != null && qty >= limit;
    setPlusLockStateForWidget(widget, locked, limit);
  }

  function resolveCartJsonItemFromState(cartItems, state) {
    if (!Array.isArray(cartItems) || !state) return null;
    const keyCandidates = [state.cartItemKey, state.key].filter((v) => v != null).map((v) => String(v));
    for (const key of keyCandidates) {
      const byKey = cartItems.find((item) => String(item?.key || '') === key);
      if (byKey) return byKey;
    }

    if (state.variantId != null) {
      const variantMatches = cartItems.filter((item) => String(item?.variant_id ?? item?.id ?? '') === String(state.variantId));
      if (variantMatches.length === 1) return variantMatches[0];
      if (variantMatches.length > 1 && state.productId != null) {
        const byProduct = variantMatches.find((item) => String(item?.product_id ?? '') === String(state.productId));
        if (byProduct) return byProduct;
      }
      if (variantMatches.length > 0) return variantMatches[0];
    }

    return null;
  }

  function fetchAuthoritativeQtyForState(state) {
    if (!state) return Promise.resolve(null);
    return fetch('/cart.js', { credentials: 'same-origin' })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`cart.js ${r.status}`))))
      .then((cart) => {
        const items = Array.isArray(cart?.items) ? cart.items : [];
        const match = resolveCartJsonItemFromState(items, state);
        if (!match) return null;
        const qty = Number(match?.quantity ?? 0);
        if (!Number.isFinite(qty)) return null;
        state.cartItemKey = match?.key != null ? String(match.key) : state.cartItemKey;
        if (match?.variant_id != null) state.variantId = String(match.variant_id);
        if (match?.product_id != null) state.productId = String(match.product_id);
        return qty;
      })
      .catch(() => null);
  }

  function requestAuthoritativeQtySync(state, fallbackQty) {
    if (!state || state.authoritativeSyncInFlight) return;
    const syncVersion = state.version;
    state.authoritativeSyncInFlight = true;
    const key = state.key;
    fetchAuthoritativeQtyForState(state)
      .then((qty) => {
        if (state.version !== syncVersion) return;
        if (Number.isFinite(qty)) {
          state.desiredQty = qty;
          state.preferDesiredUntilServerMatch = true;
          syncQtyWidgetFromResolvedQty(key, qty);
          return;
        }
        if (Number.isFinite(fallbackQty)) {
          state.desiredQty = fallbackQty;
          state.preferDesiredUntilServerMatch = false;
          syncQtyWidgetFromResolvedQty(key, fallbackQty);
        }
      })
      .finally(() => {
        state.authoritativeSyncInFlight = false;
      });
  }

  function markQtyMutationSettledByKey(key) {
    const state = qtyMutationStateByKey.get(key);
    if (!state) return;
    clearQtyMutationSettleTimer(state);
    if (!state.inFlight) return;
    const sentVersion = state.inFlightVersion || 0;
    state.inFlight = false;
    state.inFlightQty = null;
    state.inFlightVersion = 0;
    setTimeout(() => {
      reconcileQtyMutationByKey(key, { sentVersion });
    }, 40);
  }

  function maybeSendQtyMutation(state) {
    if (!state || state.inFlight) return;
    if (!Number.isFinite(state.desiredQty)) return;
    if (state.debounceT) return;

    const item = resolveSmartCartItemByKey(state.key) || state.lastItem;
    if (!item) return;
    state.lastItem = item;
    state.cartItemKey = item?.key != null ? String(item.key) : item?.item_key != null ? String(item.item_key) : state.cartItemKey;
    const variantIdRaw = item?.variant_id ?? item?.variant?.id;
    if (variantIdRaw != null) state.variantId = String(variantIdRaw);
    const productIdRaw = item?.product_id ?? item?.product?.id;
    if (productIdRaw != null) state.productId = String(productIdRaw);

    const currentQty = Number(item?.quantity || 0);
    if (currentQty === state.desiredQty) {
      state.inFlight = false;
      state.inFlightQty = null;
      state.inFlightVersion = 0;
      state.preferDesiredUntilServerMatch = false;
      syncQtyWidgetFromItem(item, state.key);
      return;
    }

    state.inFlight = true;
    state.inFlightQty = state.desiredQty;
    state.inFlightVersion = state.version;
    beginIgnoreCartChangesForQtyEdit();

    let maybePromise;
    try {
      maybePromise = window.Rebuy?.Cart?.setItemQuantity?.(item, state.desiredQty);
    } catch (_) {
      qtyEditIgnoreCartChanges = 0;
      state.inFlight = false;
      state.inFlightQty = null;
      state.inFlightVersion = 0;
      return;
    }

    clearQtyMutationSettleTimer(state);
    state.settleT = setTimeout(() => {
      markQtyMutationSettledByKey(state.key);
    }, QTY_MUTATION_SETTLE_FALLBACK_MS);

    if (maybePromise && typeof maybePromise.then === 'function') {
      maybePromise
        .catch(() => {})
        .finally(() => {
          markQtyMutationSettledByKey(state.key);
        });
    }
  }

  function queueQtyMutation(item, nextQty, options = {}) {
    const immediate = !!options.immediate;
    const state = ensureQtyMutationState(item);
    if (!state) {
      try {
        beginIgnoreCartChangesForQtyEdit();
        window.Rebuy?.Cart?.setItemQuantity?.(item, nextQty);
      } catch (_) {
        qtyEditIgnoreCartChanges = 0;
      }
      return;
    }
    state.desiredQty = nextQty;
    state.version += 1;
    state.preferDesiredUntilServerMatch = false;

    if (immediate || qtyMutationDebounceMs() === 0) {
      clearQtyMutationDebounceTimer(state);
      maybeSendQtyMutation(state);
      return;
    }

    clearQtyMutationDebounceTimer(state);
    state.debounceT = setTimeout(() => {
      state.debounceT = null;
      maybeSendQtyMutation(state);
    }, qtyMutationDebounceMs());
  }

  function reconcileQtyMutationByKey(key, meta = {}) {
    const state = qtyMutationStateByKey.get(key);
    if (!state) return;

    const item = resolveSmartCartItemByKey(key) || state.lastItem;
    if (!item) {
      if (!state.inFlight) qtyMutationStateByKey.delete(key);
      return;
    }
    state.lastItem = item;
    state.cartItemKey = item?.key != null ? String(item.key) : item?.item_key != null ? String(item.item_key) : state.cartItemKey;
    const variantIdRaw = item?.variant_id ?? item?.variant?.id;
    if (variantIdRaw != null) state.variantId = String(variantIdRaw);
    const productIdRaw = item?.product_id ?? item?.product?.id;
    if (productIdRaw != null) state.productId = String(productIdRaw);

    const serverQty = Number(item?.quantity || 0);

    if (state.inFlight && state.inFlightQty != null && serverQty === state.inFlightQty) {
      state.inFlight = false;
      state.inFlightQty = null;
      state.inFlightVersion = 0;
      clearQtyMutationSettleTimer(state);
      syncQtyWidgetFromItem(item, key);
    }

    if (state.inFlight) return;

    if (!Number.isFinite(state.desiredQty)) {
      state.desiredQty = serverQty;
      state.preferDesiredUntilServerMatch = false;
      syncQtyWidgetFromItem(item, key);
      return;
    }

    if (serverQty === state.desiredQty) {
      state.preferDesiredUntilServerMatch = false;
      syncQtyWidgetFromItem(item, key);
      return;
    }
    if (state.debounceT) return;

    const sentVersion = Number(meta?.sentVersion || 0);
    const hasNewerIntent = sentVersion > 0 && state.version > sentVersion;
    if (hasNewerIntent) {
      maybeSendQtyMutation(state);
      return;
    }

    // Server constrained/rejected this target (e.g. stock/rules). SmartCart.items()
    // can lag under poor network, so reconcile against authoritative /cart.js first.
    clearQtyMutationDebounceTimer(state);
    requestAuthoritativeQtySync(state, serverQty);
  }

  function reconcileAllQtyMutations() {
    const keys = Array.from(qtyMutationStateByKey.keys());
    for (const key of keys) {
      reconcileQtyMutationByKey(key);
    }
  }

  function beginIgnoreCartChangesForQtyEdit() {
    const n = Number(PF_SC_CONFIG?.qtyEdit?.ignoreCartChangeEventsAfterCommit || 0);
    qtyEditIgnoreCartChanges = Number.isFinite(n) && n > 0 ? n : 0;
  }

  function closeActiveQtyEditor() {
    if (!activeQtyEditor) return;
    const { input, label, widget } = activeQtyEditor;
    if (input && input.parentNode) input.parentNode.removeChild(input);
    if (label) label.classList.remove('pf-sc-qty-label-hidden');
    if (widget) widget.classList.remove('pf-sc-qty-editing');
    activeQtyEditor = null;
  }

  function applyQtyEdit(labelEl, nextQtyRaw) {
    const conf = PF_SC_CONFIG?.qtyEdit || {};
    const min = Number(conf.min) > 0 ? Number(conf.min) : 1;
    const desired = normalizeQty(nextQtyRaw);
    if (desired == null) {
      closeActiveQtyEditor();
      return;
    }

    const item = resolveSmartCartItemFromLabel(labelEl);
    if (!item) {
      closeActiveQtyEditor();
      return;
    }
    const widget = labelEl.closest(qtyWidgetSelector());

    const currentQty = desiredQtyForItem(item) ?? Number(item?.quantity || 0);
    const tagLimit = limitFromTags(item?.product?.tags);
    const maxFallback = Number(conf.maxFallback) > 0 ? Number(conf.maxFallback) : 999;
    const max = tagLimit == null ? maxFallback : tagLimit;
    const nextQty = Math.max(min, Math.min(max, desired));

    if (nextQty === currentQty) {
      closeActiveQtyEditor();
      return;
    }

    // Optimistically reflect the committed quantity immediately so the UI
    // does not flash the previous value while Smart Cart processes updates.
    labelEl.textContent = String(nextQty);
    // Optimistically reflect lock state immediately while cart updates.
    if (widget) setPlusLockStateForWidget(widget, tagLimit != null && nextQty >= tagLimit, tagLimit);
    closeActiveQtyEditor();
    queueQtyMutation(item, nextQty, { immediate: true });
  }

  function qtyBoundsForEditor(editor) {
    const conf = PF_SC_CONFIG?.qtyEdit || {};
    const min = Number(conf.min) > 0 ? Number(conf.min) : 1;
    const maxFallback = Number(conf.maxFallback) > 0 ? Number(conf.maxFallback) : 999;
    const item = resolveSmartCartItemFromLabel(editor?.label);
    const tagLimit = limitFromTags(item?.product?.tags);
    const max = tagLimit == null ? maxFallback : tagLimit;
    return { min, max };
  }

  function qtyBoundsForItem(item) {
    const conf = PF_SC_CONFIG?.qtyEdit || {};
    const min = Number(conf.min) > 0 ? Number(conf.min) : 1;
    const maxFallback = Number(conf.maxFallback) > 0 ? Number(conf.maxFallback) : 999;
    const tagLimit = limitFromTags(item?.product?.tags);
    const max = tagLimit == null ? maxFallback : tagLimit;
    return { min, max };
  }

  function setPlusLockStateForWidget(widgetEl, locked, maxQty) {
    if (!widgetEl) return;
    const buttons = Array.from(widgetEl.querySelectorAll('.rebuy-cart__flyout-item-quantity-widget-button'));
    if (!buttons.length) return;
    const plusBtn = buttons[buttons.length - 1];
    if (!plusBtn) return;

    if (locked) {
      plusBtn.classList.add('pf-sc-qty-plus-locked');
      plusBtn.setAttribute('aria-disabled', 'true');
      if (Number.isFinite(maxQty)) plusBtn.setAttribute('title', `Max quantity reached (${maxQty})`);
      else plusBtn.setAttribute('title', 'Max quantity reached');
    } else {
      plusBtn.classList.remove('pf-sc-qty-plus-locked');
      plusBtn.removeAttribute('aria-disabled');
      plusBtn.removeAttribute('title');
    }
  }

  function updateQtyLimitUi() {
    const root = getCartItemsRoot();
    if (!root) return;
    const widgets = Array.from(root.querySelectorAll(qtyWidgetSelector()));
    for (const widget of widgets) {
      const item = resolveSmartCartItemFromQtyWidget(widget);
      if (!item) continue;
      renderQtyWidgetFromItem(widget, item);
    }
  }

  let qtyLimitUiT = null;
  function scheduleQtyLimitUiUpdate() {
    if (qtyLimitUiT) clearTimeout(qtyLimitUiT);
    // Run now and on short delays to catch async Smart Cart row rendering.
    updateQtyLimitUi();
    setTimeout(updateQtyLimitUi, 80);
    qtyLimitUiT = setTimeout(updateQtyLimitUi, 220);
  }

  function applyQtyDeltaFromWidget(widgetEl, delta) {
    const item = resolveSmartCartItemFromQtyWidget(widgetEl);
    if (!item) return;
    const current = Number(item?.quantity || 0);
    const baseQty = desiredQtyForItem(item) ?? current;
    const { min, max } = qtyBoundsForItem(item);
    const tagLimit = limitFromTags(item?.product?.tags);
    const next = Math.max(min, Math.min(max, baseQty + delta));
    if (next === baseQty) {
      if (delta > 0 && tagLimit != null && baseQty >= tagLimit) {
        setPlusLockStateForWidget(widgetEl, true, tagLimit);
      }
      return;
    }

    const label = widgetEl?.querySelector?.(qtyLabelSelector());
    if (label) label.textContent = String(next);
    setPlusLockStateForWidget(widgetEl, tagLimit != null && next >= tagLimit, tagLimit);
    queueQtyMutation(item, next);
  }

  function commitActiveQtyEditorDelta(delta) {
    if (!activeQtyEditor || !activeQtyEditor.input) return;
    const { input, label } = activeQtyEditor;
    const current = normalizeQty(input.value) ?? normalizeQty(activeQtyEditor.initialQty) ?? 1;
    const { min, max } = qtyBoundsForEditor(activeQtyEditor);
    const next = Math.max(min, Math.min(max, current + delta));
    applyQtyEdit(label, next);
  }

  function qtyButtonDelta(buttonEl) {
    if (!buttonEl) return 0;
    const widget = buttonEl.closest(qtyWidgetSelector());
    if (widget) {
      const buttons = Array.from(widget.querySelectorAll('.rebuy-cart__flyout-item-quantity-widget-button'));
      const idx = buttons.indexOf(buttonEl);
      if (idx === 0) return -1;
      if (idx === buttons.length - 1) return 1;
    }
    const text = (buttonEl.textContent || '').trim();
    if (text.includes('+')) return 1;
    if (text.includes('-')) return -1;
    return 0;
  }

  function openQtyEditorForLabel(labelEl) {
    if (!PF_SC_CONFIG?.qtyEdit?.enabled) return;
    if (!labelEl || !labelEl.matches || !labelEl.matches(qtyLabelSelector())) return;

    if (activeQtyEditor?.label === labelEl) {
      activeQtyEditor.input?.focus?.();
      activeQtyEditor.input?.select?.();
      return;
    }
    closeActiveQtyEditor();

    const widget = labelEl.closest(qtyWidgetSelector());
    if (!widget) return;

    const item = resolveSmartCartItemFromLabel(labelEl);
    const queuedQty = desiredQtyForItem(item);
    const currentQty =
      normalizeQty(queuedQty) ??
      normalizeQty(item?.quantity) ??
      normalizeQty(labelEl.textContent) ??
      PF_SC_CONFIG.qtyEdit.min;
    const input = document.createElement('input');
    input.type = 'text';
    input.className = 'pf-sc-qty-inline-input';
    input.min = String(PF_SC_CONFIG?.qtyEdit?.min || 1);
    input.value = String(currentQty);
    input.inputMode = 'numeric';
    input.pattern = '[0-9]*';
    input.setAttribute('aria-label', 'Edit quantity');

    // Mirror the live qty label dimensions so tap-to-edit feels seamless.
    const labelRect = labelEl.getBoundingClientRect();
    if (labelRect.width > 0) input.style.width = `${Math.round(labelRect.width)}px`;
    if (labelRect.height > 0) input.style.height = `${Math.round(labelRect.height)}px`;
    if (labelRect.height > 0) input.style.lineHeight = `${Math.round(labelRect.height)}px`;
    input.style.flex = '0 0 auto';

    labelEl.classList.add('pf-sc-qty-label-hidden');
    widget.classList.add('pf-sc-qty-editing');
    labelEl.insertAdjacentElement('afterend', input);

    activeQtyEditor = { label: labelEl, input, widget, initialQty: currentQty };

    input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' && PF_SC_CONFIG?.qtyEdit?.commitOnEnter) {
        e.preventDefault();
        applyQtyEdit(labelEl, input.value);
        return;
      }
      if (e.key === 'Escape' && PF_SC_CONFIG?.qtyEdit?.cancelOnEscape) {
        e.preventDefault();
        closeActiveQtyEditor();
      }
    });

    input.addEventListener('blur', () => {
      if (!PF_SC_CONFIG?.qtyEdit?.commitOnBlur) {
        closeActiveQtyEditor();
        return;
      }
      // Let focus settle so click interactions (e.g. +/-) can complete.
      setTimeout(() => {
        if (!activeQtyEditor || activeQtyEditor.input !== input) return;
        applyQtyEdit(labelEl, input.value);
      }, 0);
    });

    const focusAndSelect = () => {
      if (!input.isConnected) return;
      input.focus();
      input.select();
      const len = input.value.length;
      if (typeof input.setSelectionRange === 'function') {
        try {
          input.setSelectionRange(0, len);
        } catch (_) {}
      }
    };
    requestAnimationFrame(focusAndSelect);
    // Fallback: some Smart Cart DOM updates can steal focus on the first frame.
    setTimeout(focusAndSelect, 0);
    setTimeout(focusAndSelect, 30);
    setTimeout(focusAndSelect, 80);
  }

  function bindInlineQtyEditHandlers() {
    if (window.__PF_SC_QTY_EDIT_BOUND__) return;
    window.__PF_SC_QTY_EDIT_BOUND__ = true;

    document.addEventListener(
      'keydown',
      (e) => {
        if (!activeQtyEditor) return;
        if (e.key !== 'Escape') return;
        // Keep Escape scoped to the inline editor; do not let Smart Cart close.
        e.preventDefault();
        e.stopPropagation();
        closeActiveQtyEditor();
      },
      true
    );

    document.addEventListener(
      'keyup',
      (e) => {
        if (!activeQtyEditor) return;
        if (e.key !== 'Escape') return;
        e.preventDefault();
        e.stopPropagation();
      },
      true
    );

    document.addEventListener(
      'pointerdown',
      (e) => {
        const btn =
          e.target && e.target.closest
            ? e.target.closest('.rebuy-cart__flyout-item-quantity-widget-button')
            : null;
        if (!btn) return;
        if (!isInsideSmartCartFlyout(btn)) return;
        const widget = btn.closest(qtyWidgetSelector());
        if (!widget) return;
        // Always block native qty widget interaction so bridge-owned logic
        // enforces tag limits consistently for both +/- and inline editing.
        e.preventDefault();
      },
      true
    );

    document.addEventListener(
      'click',
      (e) => {
        const btn =
          e.target && e.target.closest
            ? e.target.closest('.rebuy-cart__flyout-item-quantity-widget-button')
            : null;
        if (btn && !isInsideSmartCartFlyout(btn)) return;
        if (btn && activeQtyEditor) {
          const widget = btn.closest(qtyWidgetSelector());
          if (widget && widget === activeQtyEditor.widget) {
            const delta = qtyButtonDelta(btn);
            if (delta !== 0) {
              e.preventDefault();
              e.stopPropagation();
              commitActiveQtyEditorDelta(delta);
              return;
            }
          }
        }

        if (btn) {
          const widget = btn.closest(qtyWidgetSelector());
          const delta = qtyButtonDelta(btn);
          if (widget && delta !== 0) {
            e.preventDefault();
            e.stopPropagation();
            applyQtyDeltaFromWidget(widget, delta);
            return;
          }
        }

        const label = e.target && e.target.closest ? e.target.closest(qtyLabelSelector()) : null;
        if (!label) return;
        if (!isInsideSmartCartFlyout(label)) return;
        // Prevent Rebuy's native qty handlers from treating this as +/- interaction.
        e.preventDefault();
        e.stopPropagation();
        openQtyEditorForLabel(label);
      },
      true
    );

    document.addEventListener(
      'rebuy:cart.hide',
      () => {
        closeActiveQtyEditor();
      },
      true
    );
    document.addEventListener(
      'rebuy:cart.close',
      () => {
        closeActiveQtyEditor();
      },
      true
    );
    document.addEventListener(
      'rebuy:smartcart.hide',
      () => {
        closeActiveQtyEditor();
      },
      true
    );
  }

  /* ================================
     2) VIP progress bar updater
     ================================ */
  let vipProgressT = null;
  let vipProgressInFlight = false;

  function pfVipTierInfo(vipTier) {
    const tierKey = String(vipTier || '0');
    const conf = PF_SC_CONFIG.vip;
    const tier =
      (conf && conf.tiers && conf.tiers[tierKey]) ||
      (conf && conf.tiers && conf.tiers['0']) || { name: 'Villager', className: 'pf-vip-title--villager' };
    return { tierKey, tierName: tier.name, tierClass: tier.className };
  }

  function pfVipTemplate(str, vars) {
    return String(str).replace(/\{(\w+)\}/g, (_, k) => (vars[k] != null ? String(vars[k]) : ''));
  }

  function pfVipProgressBarUpdate() {
    const root = document.querySelector('[data-rebuy-component-id="tiered_progress_bar"]');
    if (!root) return;
    if (vipProgressInFlight) return;
    vipProgressInFlight = true;

    fetch('/cart.js', { credentials: 'same-origin' })
      .then((r) => r.json())
      .then((cart) => {
        const attrs = cart?.attributes || {};

        const vipTier = String(attrs.pf_vip_tier || '0');
        const thresholdCentsRaw = parseInt(attrs.pf_vip_free_shipping_threshold_cents, 10);
        const thresholdCents = Number.isFinite(thresholdCentsRaw)
          ? thresholdCentsRaw
          : PF_SC_CONFIG.vip.defaultThresholdCents;

        const subtotalCents =
          typeof cart.items_subtotal_price === 'number'
            ? cart.items_subtotal_price
            : typeof cart.total_price === 'number'
              ? cart.total_price
              : 0;

        const prompt = root.querySelector('#rebuy-cart__progress-bar-meter-label');
        if (!prompt) return;

        const { tierClass, tierName } = pfVipTierInfo(vipTier);
        const freeShippingHtml = PF_SC_CONFIG.vip.templates.freeShippingHtml;
        const alwaysFree =
          (PF_SC_CONFIG.vip.alwaysFreeTiers || []).includes(String(vipTier)) || thresholdCents <= 0;
        const reached = !alwaysFree && thresholdCents > 0 && subtotalCents >= thresholdCents;

        // --- markers for CSS-driven fill ---
        if (alwaysFree) root.setAttribute('data-pf-vip-free-shipping', 'true');
        else root.removeAttribute('data-pf-vip-free-shipping');

        if (reached) root.setAttribute('data-pf-threshold-reached', 'true');
        else root.removeAttribute('data-pf-threshold-reached');

        // Scaled progress % (VIP2 uses 20000, VIP0/1 uses 30000)
        if (!alwaysFree && thresholdCents > 0) {
          const pct = Math.max(0, Math.min(100, (subtotalCents / thresholdCents) * 100));
          root.style.setProperty('--pf-progress-pct', pct.toFixed(2) + '%');
          root.setAttribute('data-pf-scaled-progress', 'true');
        } else {
          root.removeAttribute('data-pf-scaled-progress');
        }

        // --- messaging ---
        if (alwaysFree) {
          prompt.innerHTML = pfVipTemplate(PF_SC_CONFIG.vip.templates.alwaysFree, {
            tierClass,
            tierName,
            freeShipping: freeShippingHtml,
          });
          return;
        }

        const awayCents = Math.max(0, thresholdCents - subtotalCents);
        const awayDollars = (awayCents / 100).toFixed(2);

        prompt.innerHTML =
          awayCents <= 0
            ? pfVipTemplate(PF_SC_CONFIG.vip.templates.reached, {
                tierClass,
                tierName,
                freeShipping: freeShippingHtml,
              })
            : pfVipTemplate(PF_SC_CONFIG.vip.templates.away, {
                tierClass,
                tierName,
                awayDollars,
                freeShipping: freeShippingHtml,
              });
      })
      .catch(() => {})
      .finally(() => {
        vipProgressInFlight = false;
      });
  }

  function scheduleVipProgressUpdate() {
    if (vipProgressT) clearTimeout(vipProgressT);
    vipProgressT = setTimeout(() => {
      if (hasActiveQtyMutations()) {
        scheduleVipProgressUpdate();
        return;
      }
      pfVipProgressBarUpdate();
      // Covers async SmartCart re-render
      setTimeout(() => {
        if (!hasActiveQtyMutations()) pfVipProgressBarUpdate();
      }, 150);
    }, 50);
  }


  function expandPeekToFull() {
    if (!isPeekingNow()) return;

    // Cancel any pending slide-away + exit peek mode.
    try {
      clearTimeout(window.__PF_SC_PEEK_T);
      clearTimeout(window.__PF_SC_PEEK_HIDE_T);
    } catch (_) {}

    // Full-screen treatment for the flyout (mobile CSS).
    HTML.classList.add(PEEK_EXPANDED_CLASS);
    HTML.classList.add(OPEN_CLASS);
    HTML.classList.add(OVERLAY_CLASS);
    // Ensure we're in the "in" state (so we animate from the current peek position).
    HTML.classList.remove(PEEK_OUT_CLASS);
    HTML.classList.add(PEEK_IN_CLASS);

    // Ask SmartCart to "normalize" its state (ensures it stays open even if peek logic was going to hide).
    try {
      if (hasSmartCart()) window.Rebuy.SmartCart.show();
    } catch (_) {}
  }

  function clearOpenMarkTimer() {
    if (openMarkT) clearTimeout(openMarkT);
    openMarkT = null;
  }

  function clearOverlayMarkTimer() {
    if (overlayMarkT) clearTimeout(overlayMarkT);
    overlayMarkT = null;
  }

  function isFlyoutVisibleNow() {
    const flyout = document.querySelector('.rebuy-cart__flyout');
    if (!flyout) return false;
    const cs = window.getComputedStyle(flyout);
    if (cs.display === 'none' || cs.visibility === 'hidden') return false;
    if (Number(cs.opacity || '1') <= 0) return false;
    const rect = flyout.getBoundingClientRect();
    return rect.width > 0 && rect.height > 0;
  }

  function markOpenSoon() {
    clearOpenMarkTimer();
    openMarkT = setTimeout(() => {
      const isPeeking =
        HTML.classList.contains(PEEK_IN_CLASS) || HTML.classList.contains(PEEK_OUT_CLASS);
      if (isPeeking) return;
      HTML.classList.add(OPEN_CLASS);
    }, OPEN_MARK_DELAY_MS);
  }

  function markOverlaySoon() {
    clearOverlayMarkTimer();
    overlayMarkT = setTimeout(() => {
      const isPeeking =
        HTML.classList.contains(PEEK_IN_CLASS) || HTML.classList.contains(PEEK_OUT_CLASS);
      if (isPeeking) return; // peek scrim is handled via CSS, not overlay class
      if (!isFlyoutVisibleNow()) return; // don't flash scrim for suppressed opens
      HTML.classList.add(OVERLAY_CLASS);
    }, OVERLAY_MARK_DELAY_MS);
  }

  function openSmartCartManual() {
    clearPeekState();
    HTML.classList.remove(PEEK_EXPANDED_CLASS);
    // Manual open marker (used by our CSS so we don't affect add-to-cart auto-open behavior).
    HTML.classList.add(MANUAL_CLASS);
    HTML.classList.add(OPEN_CLASS);

    // If your injected CSS gates the backdrop behind this class, this forces it ON for manual opens.
    HTML.classList.add(OVERLAY_CLASS);
    desktopAddAllowed = true; // manual opens should never be suppressed

    try {
      window.Rebuy.SmartCart.show();
    } catch (_) {}
  }

  // Capture phase so we can beat theme drawer handlers.
  function bindCartIconOpen() {
    if (window.__PF_SC_CART_ICON_BOUND__) return;
    window.__PF_SC_CART_ICON_BOUND__ = true;

    document.addEventListener(
      'click',
      (e) => {
        const trigger =
          e.target && e.target.closest ? e.target.closest('#cart-icon-bubble') : null;
        if (!trigger) return;

        if (!hasSmartCart()) return; // fall back to theme behavior (/cart or drawer)

        e.preventDefault();
        e.stopPropagation();

        openSmartCartManual();
      },
      true
    );
  }

  // Best-effort cleanup when SmartCart closes (event names vary across implementations).
  function onSmartCartClosed() {
    clearOpenMarkTimer();
    clearOverlayMarkTimer();
    HTML.classList.remove(OVERLAY_CLASS);
    HTML.classList.remove(MANUAL_CLASS);
    HTML.classList.remove(OPEN_CLASS);
    HTML.classList.remove(PEEK_EXPANDED_CLASS);
    clearPeekState();
  }
  document.addEventListener('rebuy:cart.hide', onSmartCartClosed);
  document.addEventListener('rebuy:cart.close', onSmartCartClosed);
  document.addEventListener('rebuy:smartcart.hide', onSmartCartClosed);

  // When SmartCart opens via non-manual paths, optionally enable scrim.
  // We intentionally do NOT enable scrim during peek animations.
  function onSmartCartOpened() {
    const isPeeking =
      HTML.classList.contains(PEEK_IN_CLASS) || HTML.classList.contains(PEEK_OUT_CLASS);
    if (isPeeking) return;

    // Desktop: enforce "show on nth add" gating (manual opens always allowed).
    if (!isMobile() && !desktopAddAllowed && !HTML.classList.contains(MANUAL_CLASS)) {
      // Ensure no scrim flash.
      clearOverlayMarkTimer();
      HTML.classList.remove(OVERLAY_CLASS);
      HTML.classList.remove(OPEN_CLASS);
      try {
        window.Rebuy?.SmartCart?.hide?.();
      } catch (_) {}
      return;
    }

    markOverlaySoon();
    // Defer marking "open" so suppressed auto-opens (that immediately hide) don't flash on-screen.
    markOpenSoon();
  }
  document.addEventListener('rebuy:cart.show', onSmartCartOpened);
  document.addEventListener('rebuy:cart.open', onSmartCartOpened);
  document.addEventListener('rebuy:smartcart.show', onSmartCartOpened);

  // Desktop: match mobile "show on nth add" rules (full open, no peek needed).
  // Many SmartCart setups auto-open on add-to-cart; we suppress those opens unless the add count is allowed.
  function runNthAddGate() {
    gateT = null;
    if (HTML.classList.contains(MANUAL_CLASS)) return; // don't fight manual opens

    // Prefer event action if present; otherwise fall back to qty delta.
    const action = lastGateAction;
    lastGateAction = null;

    const currentQty = scTotalQty();
    const lastQty = Number(sessionStorage.getItem(PF_SC_LAST_QTY_KEY) || String(currentQty));
    try {
      sessionStorage.setItem(PF_SC_LAST_QTY_KEY, String(currentQty));
    } catch (_) {}

    const isAdd = action === 'add' || (action == null && currentQty > lastQty);
    if (!isAdd) return;

    const next = Number(sessionStorage.getItem(PF_SC_ADD_COUNT_KEY) || '0') + 1;
    try {
      sessionStorage.setItem(PF_SC_ADD_COUNT_KEY, String(next));
    } catch (_) {}

    const allowed = shouldShowOnNthAdd(next);

    if (isMobile()) {
      // Mobile: "peek" on allowed adds, otherwise suppress the auto-open.
      if (!allowed) {
        clearOverlayMarkTimer();
        HTML.classList.remove(OVERLAY_CLASS);
        HTML.classList.remove(OPEN_CLASS);
        HTML.classList.remove(PEEK_EXPANDED_CLASS);
        clearPeekState();
        try {
          window.Rebuy?.SmartCart?.hide?.();
        } catch (_) {}
        return;
      }

      // Allowed on mobile: show as peek (no full-overlay scrim, no click-blocking backdrop).
      HTML.classList.add(OPEN_CLASS);
      HTML.classList.remove(OVERLAY_CLASS);
      HTML.classList.remove(PEEK_OUT_CLASS);
      HTML.classList.add(PEEK_IN_CLASS);

      // Ensure flyout exists (some setups only mount on show()).
      try {
        window.Rebuy?.SmartCart?.show?.();
      } catch (_) {}

      // Slide away after a bit unless the user interacts (bridge cancels __PF_SC_PEEK_T / __PF_SC_PEEK_HIDE_T).
      try {
        clearTimeout(window.__PF_SC_PEEK_T);
        clearTimeout(window.__PF_SC_PEEK_HIDE_T);
      } catch (_) {}

      window.__PF_SC_PEEK_T = setTimeout(() => {
        HTML.classList.remove(PEEK_IN_CLASS);
        HTML.classList.add(PEEK_OUT_CLASS);
        window.__PF_SC_PEEK_HIDE_T = setTimeout(() => {
          try {
            window.Rebuy?.SmartCart?.hide?.();
          } catch (_) {}
          HTML.classList.remove(PEEK_OUT_CLASS);
          HTML.classList.remove(OPEN_CLASS);
        }, PF_SC_CONFIG.mobilePeek.slideMs);
      }, PF_SC_CONFIG.mobilePeek.peekMs);
      return;
    }

    // Desktop: full open on allowed adds (no peek), suppress on disallowed adds.
    desktopAddAllowed = allowed;

    if (!allowed) {
      // Suppress any auto-open immediately (prevents "fly in then fly out").
      clearOverlayMarkTimer();
      HTML.classList.remove(OVERLAY_CLASS);
      HTML.classList.remove(OPEN_CLASS);
      try {
        window.Rebuy?.SmartCart?.hide?.();
      } catch (_) {}
    } else {
      // Allowed add on desktop: ensure it's open (we may have pre-suppressed the auto-open).
      try {
        window.Rebuy?.SmartCart?.show?.();
      } catch (_) {}
    }
  }

  function scheduleNthAddGate(e) {
    if (qtyEditIgnoreCartChanges > 0) {
      qtyEditIgnoreCartChanges -= 1;
      lastGateAction = null;
      initQtyBaseline();
      return;
    }

    if (HTML.classList.contains(MANUAL_CLASS)) return; // don't fight manual opens

    // Save the latest action; SmartCart.items() often lags this event.
    const action = e?.detail?.action;
    lastGateAction = action ?? lastGateAction;

    // Desktop: pre-suppress SmartCart auto-open until the nth-add gate decides.
    // We do this not only for explicit "add" actions but also when action is missing/unknown,
    // because some setups omit `event.detail.action` and we'd otherwise allow a brief open
    // before the delayed gate hides it.
    if (!isMobile() && (action === 'add' || action == null)) {
      desktopAddAllowed = false;
      clearOverlayMarkTimer();
      HTML.classList.remove(OVERLAY_CLASS);
      HTML.classList.remove(OPEN_CLASS);
      try {
        window.Rebuy?.SmartCart?.hide?.();
      } catch (_) {}
    }

    if (gateT) clearTimeout(gateT);
    gateT = setTimeout(runNthAddGate, SC_GATE_DELAY_MS);
  }
  function bindCartChangeHandlers() {
    if (window.__PF_SC_CART_CHANGE_BOUND__) return;
    window.__PF_SC_CART_CHANGE_BOUND__ = true;
    document.addEventListener('rebuy:cart.change', scheduleNthAddGate);
    document.addEventListener('rebuy:cart.change', reconcileAllQtyMutations);
    document.addEventListener('rebuy:cart.change', scheduleItemQuantityLimits);
    document.addEventListener('rebuy:cart.change', scheduleVipProgressUpdate);
    document.addEventListener('rebuy:cart.change', scheduleQtyLimitUiUpdate);
    document.addEventListener('rebuy:cart.change', closeActiveQtyEditor);
  }

  function bindVipOpenHandlers() {
    if (window.__PF_SC_VIP_OPEN_BOUND__) return;
    window.__PF_SC_VIP_OPEN_BOUND__ = true;
    document.addEventListener('rebuy:cart.show', scheduleVipProgressUpdate);
    document.addEventListener('rebuy:cart.open', scheduleVipProgressUpdate);
    document.addEventListener('rebuy:smartcart.show', scheduleVipProgressUpdate);
    document.addEventListener('rebuy:smartcart.ready', scheduleVipProgressUpdate);
    // Also enforce quantity limits when SmartCart opens (catches limits missed while cart was closed)
    document.addEventListener('rebuy:cart.show', scheduleItemQuantityLimits);
    document.addEventListener('rebuy:cart.open', scheduleItemQuantityLimits);
    document.addEventListener('rebuy:smartcart.show', scheduleItemQuantityLimits);
    document.addEventListener('rebuy:cart.show', reconcileAllQtyMutations);
    document.addEventListener('rebuy:cart.open', reconcileAllQtyMutations);
    document.addEventListener('rebuy:smartcart.show', reconcileAllQtyMutations);
    document.addEventListener('rebuy:smartcart.ready', reconcileAllQtyMutations);
    document.addEventListener('rebuy:cart.show', scheduleQtyLimitUiUpdate);
    document.addEventListener('rebuy:cart.open', scheduleQtyLimitUiUpdate);
    document.addEventListener('rebuy:smartcart.show', scheduleQtyLimitUiUpdate);
    document.addEventListener('rebuy:smartcart.ready', scheduleQtyLimitUiUpdate);
  }

  // If the cart is in peek mode and the user interacts with it, expand to full and cancel slide-away.
  // - Tap anywhere inside the flyout expands immediately.
  // - Upward swipe expands (if tap didn't already fire).
  let touchStartY = null;
  let expandedThisGesture = false;

  function resetGesture() {
    touchStartY = null;
    expandedThisGesture = false;
  }

  function bindPeekExpandHandlers() {
    if (window.__PF_SC_PEEK_EXPAND_BOUND__) return;
    window.__PF_SC_PEEK_EXPAND_BOUND__ = true;

    // Pointer/touch down inside the flyout = intent to interact.
    document.addEventListener(
      'pointerdown',
      (e) => {
        if (!isPeekingNow()) return;
        const flyout =
          e.target && e.target.closest ? e.target.closest('.rebuy-cart__flyout') : null;
        if (!flyout) return;
        expandPeekToFull();
      },
      { capture: true }
    );

    document.addEventListener(
      'touchstart',
      (e) => {
        if (!isPeekingNow()) return;
        const t = e.touches && e.touches[0];
        if (!t) return;
        const flyout =
          e.target && e.target.closest ? e.target.closest('.rebuy-cart__flyout') : null;
        if (!flyout) return;
        touchStartY = t.clientY;
        expandedThisGesture = false;
      },
      { passive: true, capture: true }
    );

    document.addEventListener(
      'touchmove',
      (e) => {
        if (!isPeekingNow()) return;
        if (expandedThisGesture) return;
        if (touchStartY == null) return;
        const t = e.touches && e.touches[0];
        if (!t) return;
        const flyout =
          e.target && e.target.closest ? e.target.closest('.rebuy-cart__flyout') : null;
        if (!flyout) return;
        const dy = t.clientY - touchStartY;
        // Swipe up threshold
        if (dy < -24) {
          expandedThisGesture = true;
          expandPeekToFull();
        }
      },
      { passive: true, capture: true }
    );

    document.addEventListener('touchend', resetGesture, { passive: true, capture: true });
    document.addEventListener('touchcancel', resetGesture, { passive: true, capture: true });
  }

  function bindCoreSmartCartHandlers() {
    if (window.__PF_SMARTCART_BRIDGE_BOUND__) return;
    window.__PF_SMARTCART_BRIDGE_BOUND__ = true;

    bindCartIconOpen();
    bindCartChangeHandlers();
    bindVipOpenHandlers();
    bindPeekExpandHandlers();
    bindInlineQtyEditHandlers();

    initQtyBaseline();
    scheduleVipProgressUpdate();
    scheduleQtyLimitUiUpdate();
  }

  // Bind everything off the Smart Cart "ready" event (recommended by Rebuy docs).
  // https://developers.rebuyengine.com/reference/smart-cart-event-listeners
  document.addEventListener('rebuy:smartcart.ready', bindCoreSmartCartHandlers, { once: true });

  // Fallback: if the theme script loads after SmartCart is ready (event won't replay),
  // bind once we detect SmartCart is present.
  bindCartIconOpen();
  setTimeout(() => {
    if (hasSmartCart()) bindCoreSmartCartHandlers();
  }, 0);
})();
